import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { DiagramasService } from '../../../shared/services/api/c4-model/DiagramasService';
import { Box, Skeleton } from '@mui/material';
import { PageLayout } from '../../../shared/layouts';
import { ApiException } from '../../../shared/services/api/ApiException';
import { feedback } from '../../../shared/services';
import { useDebounce } from '../../../shared/hooks';
import { useNavigate } from 'react-router-dom';

const EMPTY_TEXT_SHA256 = '47DEQpj8HBSa+/TImW+5JCeuQeRkm5NMpJWZG3hSuFU=';
// Reuse the app's unified service worker to avoid replacing another worker at scope '/'.
const DIAGRAM_SW_URL = '/service-worker.js';

function stripCspMetaTags(html: string): string {
  return html.replace(/<meta[^>]*http-equiv=["']Content-Security-Policy["'][^>]*>/gi, '');
}

function normalizeDiagramHtml(html: string): string {
  let normalized = html;

  // Remove any CSP from API payload so we control CSP from SW response headers.
  normalized = stripCspMetaTags(normalized);

  // Replace plugin-based object embedding with img to avoid object-src violations
  // and reduce attack surface from plugin documents.
  normalized = normalized.replace(
    /<object([^>]*\bid=["']svgViewer["'][^>]*)><\/object>/i,
    '<img$1 src="" alt="diagrama-svg" />'
  );
  normalized = normalized.replace(
    /<object([^>]*\bid=["']svgViewer["'][^>]*?)\btype=["']image\/svg\+xml["']([^>]*)><\/object>/i,
    '<img$1$2 src="" alt="diagrama-svg" />'
  );

  // Keep existing API script working after object->img migration.
  normalized = normalized.replace(/setAttribute\(['"]data['"]\s*,\s*file\)/g, "setAttribute('src', file)");

  return normalized;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function sha256Base64(content: string): Promise<string> {
  const encoded = new TextEncoder().encode(content);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return arrayBufferToBase64(digest);
}

function extractInlineStyleContents(html: string): string[] {
  const contents: string[] = [];
  const styleRe = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
  let match: RegExpExecArray | null;

  while ((match = styleRe.exec(html)) !== null) {
    contents.push(match[1] || '');
  }

  return contents;
}

function extractInlineScriptContents(html: string): string[] {
  const contents: string[] = [];
  const scriptRe = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
  let match: RegExpExecArray | null;

  while ((match = scriptRe.exec(html)) !== null) {
    const attrs = match[1] || '';
    const hasSrc = /\bsrc\s*=\s*["'][^"']+["']/i.test(attrs);
    if (!hasSrc) {
      contents.push(match[2] || '');
    }
  }

  return contents;
}

function extractInlineEventHandlerContents(html: string): string[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const handlers: string[] = [];
  const allElements = doc.querySelectorAll('*');

  allElements.forEach((el) => {
    for (let i = 0; i < el.attributes.length; i++) {
      const attr = el.attributes[i];
      if (attr.name && attr.name.toLowerCase().startsWith('on') && attr.value) {
        handlers.push(attr.value);
      }
    }
  });

  return handlers;
}

async function buildDiagramCsp(html: string): Promise<string> {
  const styleHashes = new Set<string>([EMPTY_TEXT_SHA256]);
  const scriptHashes = new Set<string>();
  const eventHandlerHashes = new Set<string>();

  const styleContents = extractInlineStyleContents(html);
  for (const content of styleContents) {
    const hash = await sha256Base64(content);
    styleHashes.add(hash);
  }

  const scriptContents = extractInlineScriptContents(html);
  for (const content of scriptContents) {
    const hash = await sha256Base64(content);
    scriptHashes.add(hash);
  }

  const eventHandlerContents = extractInlineEventHandlerContents(html);
  for (const content of eventHandlerContents) {
    const hash = await sha256Base64(content);
    eventHandlerHashes.add(hash);
  }

  const styleHashTokens = Array.from(styleHashes).map((hash) => `'sha256-${hash}'`).join(' ');
  const scriptTokens = new Set<string>();
  scriptHashes.forEach((hash) => scriptTokens.add(`'sha256-${hash}'`));
  eventHandlerHashes.forEach((hash) => scriptTokens.add(`'sha256-${hash}'`));

  const hasEventHandlerHashes = eventHandlerHashes.size > 0;
  const scriptSrcParts: string[] = ["script-src 'self'"];
  if (hasEventHandlerHashes) {
    scriptSrcParts.push("'unsafe-hashes'");
  }
  if (scriptTokens.size > 0) {
    scriptSrcParts.push(Array.from(scriptTokens).join(' '));
  }
  const scriptSrc = scriptSrcParts.join(' ');

  return [
    "default-src 'self'",
    scriptSrc,
    `style-src 'self' https://fonts.googleapis.com https://cdnjs.cloudflare.com ${styleHashTokens}`,
    `style-src-elem 'self' https://fonts.googleapis.com https://cdnjs.cloudflare.com ${styleHashTokens}`,
    "style-src-attr 'none'",
    "img-src 'self' data: https:",
    "font-src 'self' data: https://fonts.gstatic.com https://cdnjs.cloudflare.com",
    "connect-src 'none'",
    "form-action 'none'",
    "frame-ancestors 'self'",
    "object-src 'none'",
    "base-uri 'none'",
  ].join('; ');
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const Diagramas: React.FC = () => {
  const { debounce } = useDebounce();
  const [diagramas, setDiagramas] = React.useState<string | null>(null);
  const [diagramasRoute, setDiagramasRoute] = React.useState<string | null>(null);
  const swScope = '/';
  const [open, setOpen] = React.useState(true);
  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
    navigate('/c4-sistemas');
  };

  React.useEffect(() => {
    debounce(() => {
      DiagramasService.getDiagramas().then((result) => {
        if (result instanceof ApiException) {
          feedback(result.message, 'error');
        } else {
          setDiagramas(result);
        }
      });
    });
  }, [debounce]);

  // Use a service worker to serve the diagram HTML at a same-origin URL
  // like /__diagramas/<uuid>. This avoids framing a blob: URL which can be
  // blocked by frame-src if not allowed. Your CSP allows 'self' so this
  // path is framed correctly.
  React.useEffect(() => {
    if (!diagramas) {
      setDiagramasRoute(null);
      return;
    }

    // Generate a stable id for this diagram render
    const id = crypto && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + '-' + Math.random().toString(36).slice(2, 9);
    const route = `/__diagramas/${id}`;

    // Register service worker if available and not registered yet
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register(DIAGRAM_SW_URL, { scope: swScope, updateViaCache: 'none' }).then((reg) => {
        reg.update().catch(() => {
          // Ignore update races; registration will still proceed with current worker.
        });
        // Wait for active service worker controller
        const getController = (): Promise<ServiceWorker | null> => {
          if (navigator.serviceWorker.controller) {
            return Promise.resolve(navigator.serviceWorker.controller as ServiceWorker);
          }
          return new Promise((res) => {
            const onCtrl = () => {
              navigator.serviceWorker.removeEventListener('controllerchange', onCtrl);
              res(navigator.serviceWorker.controller as ServiceWorker | null);
            };
            navigator.serviceWorker.addEventListener('controllerchange', onCtrl);
          });
        };

        getController()
          .then((controller: ServiceWorker | null) => {
            try {
              if (!controller) {
                console.error('No active service worker controller available');
                setDiagramasRoute(null);
                return;
              }
              const publishPage = async () => {
                const sanitizedHtml = normalizeDiagramHtml(diagramas);
                const csp = await buildDiagramCsp(sanitizedHtml);

                // Use MessageChannel to get ACK from SW before setting route
                const channel = new MessageChannel();
                const timeout = setTimeout(() => {
                  try {
                    channel.port1.onmessage = null;
                  } catch (e) {}
                }, 3000);
                channel.port1.onmessage = (ev) => {
                  clearTimeout(timeout);
                  const data = ev.data || {};
                  if (data && data.status === 'ok') {
                    setDiagramasRoute(route);
                  } else {
                    console.error('SW failed to store diagram page', data);
                    setDiagramasRoute(null);
                  }
                };
                controller.postMessage({ type: 'SET_PAGE', id, html: sanitizedHtml, csp }, [channel.port2]);
              };
              publishPage().catch((e) => {
                console.error('Failed to build CSP for diagram page', e);
                setDiagramasRoute(null);
              });
            } catch (e) {
              console.error('Failed to post diagram to service worker', e);
              setDiagramasRoute(null);
            }
          })
          .catch((e) => {
            console.error('Service worker controller not ready', e);
          });
      }).catch((e) => {
        console.error('Failed to register diagramas service worker', e);
        setDiagramasRoute(null);
      });
    } else {
      // Fallback: if no service worker available, leave route null
      setDiagramasRoute(null);
    }

    // cleanup: remove page entry from SW when diagram changes or unmounts
    return () => {
      try {
        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'REMOVE_PAGE', id });
        }
      } catch (e) {
        // ignore
      }
      setDiagramasRoute(null);
    };
  }, [diagramas]);

  if (!diagramas) {
    return (
      <PageLayout title="Diagramas">
        <Box p="10px">
          <Skeleton height="80vh" />
        </Box>
      </PageLayout>
    );
  }

  return (
    <Dialog
      fullScreen
      open={!!open}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Diagramas
          </Typography>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="iframe"
        title="diagramas-c4"
        src={diagramasRoute || undefined}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
        sx={{ width: '100%', height: '100%', border: 0 }}
      />
    </Dialog>
  );
};
